import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Facebook, Instagram, Building2, Gift, CheckCircle2, XCircle, AlertTriangle, Loader2, ShieldAlert, RefreshCw, LogOut, PlayCircle, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import SheetsSyncPanel from "@/components/SheetsSyncPanel";
import CommNotificationsPanel from "@/components/CommNotificationsPanel";

/**
 * Iter50 — Integrations & Activation UI (owner-facing).
 * Sections: Facebook, Instagram, Google Business Profile, Tremendous.
 * All sections show live connection status, feature-flag status, and
 * connect/disconnect/test-connection controls. Tremendous includes the
 * production activation form (typed phrase + dual cap ack) and the
 * emergency-disable kill switch button.
 */
export default function Integrations() {
  return (
    <div className="p-6 sm:p-8 space-y-8 max-w-5xl" data-testid="integrations-page">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Integrations</h1>
          <p className="text-sm text-zinc-500">Manage Facebook, Instagram, Google Business Profile, and Tremendous rewards.</p>
        </div>
      </div>
      <CommNotificationsPanel />
      <FacebookPanel />
      <SheetsSyncPanel />
      <InstagramPanel />
      <GbpPanel />
      <TremendousPanel />
    </div>
  );
}

function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3"/> : <XCircle className="w-3 h-3"/>}
      {label}
    </span>
  );
}

function Section({ title, icon: Icon, iconColor, children, testid }) {
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <h2 className="font-heading text-lg font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Facebook
// ---------------------------------------------------------------------------
function FacebookPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/facebook/config"); setCfg(data); } catch { setCfg({}); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const connect = async () => {
    try {
      const { data } = await api.get("/facebook/oauth/start");
      sessionStorage.setItem("fb_oauth_state", data.state);
      window.location.href = data.oauth_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Facebook not configured on this backend"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Facebook Page?")) return;
    try { await api.post("/facebook/disconnect"); toast.success("Disconnected"); load(); } catch { toast.error("Failed"); }
  };
  return (
    <Section title="Facebook" icon={Facebook} iconColor="text-blue-600" testid="fb-panel">
      {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={cfg?.app_configured} label={cfg?.app_configured ? "Meta app configured" : "Not configured"} />
            <StatusPill ok={cfg?.connected} label={cfg?.connected ? `Connected to ${cfg?.page_name}` : "Not connected"} />
            <StatusPill ok={cfg?.publish_enabled} label={cfg?.publish_enabled ? "Publishing enabled" : "Publishing disabled"} />
          </div>
          {cfg?.connected && <div className="text-xs text-zinc-500 font-mono-accent">Page ID: {cfg.page_id}</div>}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={connect} disabled={!cfg?.app_configured} data-testid="fb-connect-btn">
              {cfg?.connected ? "Reconnect / Change Page" : "Connect Facebook Page"}
            </Button>
            {cfg?.connected && <Button variant="outline" onClick={disconnect} data-testid="fb-disconnect-btn"><LogOut className="w-4 h-4 mr-1"/> Disconnect</Button>}
          </div>
          {!cfg?.app_configured && <div className="text-xs text-zinc-500">Requires backend env: <code>FACEBOOK_APP_ID</code>, <code>FACEBOOK_APP_SECRET</code>, <code>FACEBOOK_REDIRECT_URI</code>, <code>FACEBOOK_TOKEN_ENCRYPTION_KEY</code>.</div>}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Instagram
// ---------------------------------------------------------------------------
function InstagramPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/instagram/config"); setCfg(data); } catch { setCfg({}); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const connect = async () => {
    try {
      const { data } = await api.get("/instagram/oauth/start");
      sessionStorage.setItem("ig_oauth_state", data.state);
      window.location.href = data.oauth_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Instagram publisher not configured"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram account?")) return;
    try { await api.post("/instagram/disconnect"); toast.success("Disconnected"); setTest(null); load(); } catch { toast.error("Failed"); }
  };
  const runTest = async () => {
    try { const { data } = await api.get("/instagram/test-connection"); setTest(data); } catch (e) { setTest({ ok: false, error: e.response?.data?.detail || "Test failed" }); }
  };
  return (
    <Section title="Instagram" icon={Instagram} iconColor="text-fuchsia-600" testid="ig-panel">
      {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={cfg?.app_configured} label={cfg?.app_configured ? "Meta app configured" : "Meta app not configured"} />
            <StatusPill ok={cfg?.feature_enabled} label={cfg?.feature_enabled ? "Feature enabled" : "Feature flag off"} />
            <StatusPill ok={cfg?.connected} label={cfg?.connected ? `@${cfg?.ig_username || cfg?.ig_user_id}` : "Not connected"} />
            <StatusPill ok={cfg?.publish_enabled} label={cfg?.publish_enabled ? "Publishing enabled" : "Publishing disabled"} />
          </div>
          {cfg?.connected && <div className="text-xs text-zinc-500 font-mono-accent">IG user: {cfg.ig_user_id} · Linked Page: {cfg.linked_page_name}</div>}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={connect} disabled={!cfg?.feature_enabled || !cfg?.app_configured} data-testid="ig-connect-btn">
              {cfg?.connected ? "Reconnect / Change Account" : "Connect Instagram"}
            </Button>
            {cfg?.connected && <>
              <Button variant="outline" onClick={runTest} data-testid="ig-test-btn"><RefreshCw className="w-4 h-4 mr-1"/> Test connection</Button>
              <Button variant="outline" onClick={disconnect} data-testid="ig-disconnect-btn"><LogOut className="w-4 h-4 mr-1"/> Disconnect</Button>
            </>}
          </div>
          {test && (
            <div className={`text-sm p-3 rounded-md border ${test.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="ig-test-result">
              {test.ok ? (
                <div>
                  Token healthy · @{test.ig_username} · {test.account_type} · {test.followers_count ?? "—"} followers · {test.media_count ?? "—"} media
                </div>
              ) : (<div>Connection failed: {test.error}</div>)}
            </div>
          )}
          {!cfg?.feature_enabled && <div className="text-xs text-zinc-500">Requires <code>INSTAGRAM_PUBLISH_ENABLED=true</code> plus Meta app IG scopes (<code>instagram_basic</code>, <code>instagram_content_publish</code>).</div>}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Google Business Profile
// ---------------------------------------------------------------------------
function GbpPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/gbp/config"); setCfg(data); } catch { setCfg({}); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const connect = async () => {
    try {
      const { data } = await api.get("/gbp/oauth/start");
      sessionStorage.setItem("gbp_oauth_state", data.state);
      window.location.href = data.oauth_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Google OAuth not configured"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Business Profile?")) return;
    try { await api.post("/gbp/disconnect"); toast.success("Disconnected"); setTest(null); load(); } catch { toast.error("Failed"); }
  };
  const runTest = async () => {
    try { const { data } = await api.get("/gbp/test-connection"); setTest(data); } catch (e) { setTest({ ok: false, error: e.response?.data?.detail || "Test failed" }); }
  };
  return (
    <Section title="Google Business Profile" icon={Building2} iconColor="text-emerald-700" testid="gbp-panel">
      {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={cfg?.app_configured} label={cfg?.app_configured ? "Google OAuth configured" : "OAuth not configured"} />
            <StatusPill ok={cfg?.publish_feature_enabled} label={cfg?.publish_feature_enabled ? "Publish enabled" : "Publish flag off"} />
            <StatusPill ok={cfg?.reviews_feature_enabled} label={cfg?.reviews_feature_enabled ? "Reviews enabled" : "Reviews flag off"} />
            <StatusPill ok={cfg?.connected} label={cfg?.connected ? (cfg?.location_title || "Connected") : "Not connected"} />
          </div>
          {cfg?.connected && <div className="text-xs text-zinc-500 font-mono-accent">Account: {cfg.account_name || cfg.account_id} · Location: {cfg.location_id}</div>}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={connect} disabled={!cfg?.app_configured || !(cfg?.publish_feature_enabled || cfg?.reviews_feature_enabled)} data-testid="gbp-connect-btn">
              {cfg?.connected ? "Reconnect / Change Location" : "Connect Google Business Profile"}
            </Button>
            {cfg?.connected && <>
              <Button variant="outline" onClick={runTest} data-testid="gbp-test-btn"><RefreshCw className="w-4 h-4 mr-1"/> Test connection</Button>
              <Button variant="outline" onClick={disconnect} data-testid="gbp-disconnect-btn"><LogOut className="w-4 h-4 mr-1"/> Disconnect</Button>
            </>}
          </div>
          {test && (
            <div className={`text-sm p-3 rounded-md border ${test.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="gbp-test-result">
              {test.ok ? (
                <div>Token healthy · Location: {test.location_title || "—"} · VoM: {test.voice_of_merchant_state || "—"}</div>
              ) : (<div>Connection failed: {test.error}</div>)}
            </div>
          )}
          {cfg?.connected && (
            <div className="text-xs text-zinc-500">
              Open the <a href="/app/reviews" className="text-emerald-700 hover:underline">Reviews page</a> to sync reviews and draft owner-approved replies.
            </div>
          )}
          {!cfg?.app_configured && <div className="text-xs text-zinc-500">Requires backend env: <code>GOOGLE_OAUTH_CLIENT_ID</code>, <code>GOOGLE_OAUTH_CLIENT_SECRET</code>, <code>GOOGLE_OAUTH_REDIRECT_URI</code>, <code>GOOGLE_TOKEN_ENCRYPTION_KEY</code>, plus <code>GBP_PUBLISH_ENABLED</code> or <code>GBP_REVIEWS_ENABLED</code>.</div>}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Tremendous
// ---------------------------------------------------------------------------
function TremendousPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");
  const [phrase, setPhrase] = useState("");
  const [ackDaily, setAckDaily] = useState(false);
  const [ackMonthly, setAckMonthly] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tremendous/spend-status"); setStatus(data); }
    catch { setStatus({}); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [prodStatus, setProdStatus] = useState(null);
  const loadProd = async () => { try { const { data } = await api.get("/tremendous/production/status"); setProdStatus(data); } catch { setProdStatus({}); } };
  useEffect(() => { loadProd(); }, []);

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/tremendous/production/activate", {
        token, typed_phrase: phrase, ack_daily_cap: ackDaily, ack_monthly_cap: ackMonthly,
      });
      toast.success("Production activated");
      setToken(""); setPhrase(""); setAckDaily(false); setAckMonthly(false);
      load(); loadProd();
    } catch (e) { toast.error(e.response?.data?.detail || "Activation failed"); }
    finally { setBusy(false); }
  };

  const deactivate = async () => {
    if (!window.confirm("Deactivate production Tremendous for this business?")) return;
    try { await api.post("/tremendous/production/deactivate"); toast.success("Deactivated"); load(); loadProd(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const emergency = async () => {
    if (!window.confirm("EMERGENCY DISABLE: turn on kill switch AND deactivate production immediately? This blocks ALL Tremendous fulfilment. Continue?")) return;
    try { await api.post("/tremendous/production/emergency-disable"); toast.success("Emergency disable engaged"); load(); loadProd(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const canActivate = !!token && phrase.trim() === (prodStatus?.confirmation_phrase || "") && ackDaily && ackMonthly && !busy;

  return (
    <Section title="Tremendous — Rewards fulfilment" icon={Gift} iconColor="text-amber-600" testid="tremendous-panel">
      {loading || !status ? <Loader2 className="w-4 h-4 animate-spin"/> : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={prodStatus?.prod_enabled_env} label={prodStatus?.prod_enabled_env ? "Prod env enabled" : "Prod env off"} />
            <StatusPill ok={prodStatus?.prod_configured} label={prodStatus?.prod_configured ? "Prod configured" : "Prod not configured"} />
            <StatusPill ok={status?.production_enabled} label={status?.production_enabled ? "Production active" : "Sandbox / manual"} />
            <StatusPill ok={status?.pilot_completed} label={status?.pilot_completed ? "Pilot completed" : "Pilot pending"} />
            <StatusPill ok={!status?.kill_switch} label={status?.kill_switch ? "KILL SWITCH ON" : "Kill switch off"} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-zinc-200 rounded-lg p-3">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Today</div>
              <div className="text-lg font-semibold">{fmt(status.daily_spend_cents)} / {fmt(status.daily_cap_cents)}</div>
              <div className="text-xs text-zinc-500">Remaining: {fmt(status.daily_remaining_cents)}</div>
            </div>
            <div className="border border-zinc-200 rounded-lg p-3">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Month-to-date</div>
              <div className="text-lg font-semibold">{fmt(status.monthly_spend_cents)} / {fmt(status.monthly_cap_cents)}</div>
              <div className="text-xs text-zinc-500">Remaining: {fmt(status.monthly_remaining_cents)}</div>
            </div>
          </div>

          {status.low_balance_alert && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4"/> Low balance: {fmt(status.low_balance_alert.available_cents)} available (threshold {fmt(status.low_balance_threshold_cents)}). Reported at {status.low_balance_alert.at}.
            </div>
          )}

          {!status.production_enabled && prodStatus?.prod_enabled_env && prodStatus?.prod_configured && (
            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50 space-y-3" data-testid="tremendous-activation-form">
              <div className="font-medium flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600"/> Production activation</div>
              <p className="text-xs text-amber-800">Ask a super-admin to run the readiness check and issue an activation token. Then paste the token here and type the confirmation phrase exactly.</p>
              <input value={token} onChange={e => setToken(e.target.value)} placeholder="Activation token" className="w-full border rounded px-3 py-2 text-sm" data-testid="tremendous-token-input"/>
              <input value={phrase} onChange={e => setPhrase(e.target.value)} placeholder="Type the confirmation phrase exactly" className="w-full border rounded px-3 py-2 text-sm" data-testid="tremendous-phrase-input"/>
              <div className="text-[11px] text-zinc-600">Required phrase: <code className="bg-white px-1 rounded">{prodStatus?.confirmation_phrase}</code></div>
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={ackDaily} onChange={e => setAckDaily(e.target.checked)} data-testid="tremendous-ack-daily"/> I understand the daily cap of {fmt(status.daily_cap_cents)} and that any fulfilment beyond it will be rejected.</label>
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={ackMonthly} onChange={e => setAckMonthly(e.target.checked)} data-testid="tremendous-ack-monthly"/> I understand the monthly cap of {fmt(status.monthly_cap_cents)} and that any fulfilment beyond it will be rejected.</label>
              <Button onClick={activate} disabled={!canActivate} data-testid="tremendous-activate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <PlayCircle className="w-4 h-4 mr-1"/>} Activate production
              </Button>
            </div>
          )}

          {(!prodStatus?.prod_enabled_env || !prodStatus?.prod_configured) && (
            <div className="text-xs text-zinc-500">Production Tremendous is not yet available on this backend. Sandbox / manual fulfilment continues to work. Set <code>TREMENDOUS_PROD_ENABLED=true</code> + production API key/funding source/campaign to enable production activation.</div>
          )}

          <div className="flex gap-2 flex-wrap">
            {status.production_enabled && <Button variant="outline" onClick={deactivate} data-testid="tremendous-deactivate-btn"><ShieldOff className="w-4 h-4 mr-1"/> Deactivate production</Button>}
            {(status.production_enabled || status.kill_switch === false) && (
              <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={emergency} data-testid="tremendous-emergency-btn">
                <ShieldAlert className="w-4 h-4 mr-1"/> EMERGENCY disable
              </Button>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}
